import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  LayersControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { FaCircleInfo } from "react-icons/fa6";
import L from "leaflet"; // Import the Leaflet library for L.geoJSON
import { MapHomeControl, MapLegendControl } from "../../common/MapControls/MapControls";

const Network = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [lineLayers, setLineLayers] = useState({});
  const [info, setInfo] = useState(false);
  const infoRef = useRef(null);
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  useEffect(() => {
    if (!info) return;
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setInfo(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [info]);
  const [mapBounds, setMapBounds] = useState(null); // New state for map bounds

  const getColorForSubdivision = useCallback((subdivision) => {
    switch (subdivision) {
      case "Road_district":
        return "green";
      case "Road_national":
        return "yellow";
      case "Road_rural":
        return "orange";
      case "Water_canal":
        return "violet";
      case "Water_drain":
        return "blue";
      case "Powerline":
        return "black";
      case "Road_other":
        return "gray";
      case "Waterbodies":
        return "darkblue";
      default:
        return "gray";
    }
  }, []);

  // Fetch and group line features by SectorialSubdivision
  useEffect(() => {
    Promise.all([
      fetch("/DATA/OSMbased_adm0_boundary.json").then((res) => res.json()),
      fetch("/cityfire/infra_line.geojson").then((res) => res.json()),
    ])
    .then(([boundary, lineData]) => {
      setBoundaryData(boundary);
      
      const grouped = {};
      lineData.features.forEach((feature) => {
        const subdivision = feature.properties?.SectorialSubdivision || "Others";
        if (!grouped[subdivision]) grouped[subdivision] = [];
        grouped[subdivision].push(feature);
      });

      const layerData = {};
      for (const [subdivision, features] of Object.entries(grouped)) {
        layerData[subdivision] = {
          type: "FeatureCollection",
          features,
        };
      }
      setLineLayers(layerData);

      // Calculate and set map bounds from the boundary data
      if (boundary && boundary.features && boundary.features.length > 0) {
        const geoJsonLayer = L.geoJSON(boundary);
        setMapBounds(geoJsonLayer.getBounds());
      }
    })
    .catch((error) => console.error("Error loading GeoJSON data:", error));
  }, []);

  // InfoButton component
  const InfoButton = ({ onClick }) => {
    // Leaflet's own drag/click handling swallows clicks on overlay controls
    // unless propagation to the map is explicitly disabled on the DOM node.
    const btnRef = useCallback((node) => {
      if (node) {
        L.DomEvent.disableClickPropagation(node);
        L.DomEvent.disableScrollPropagation(node);
      }
    }, []);
    return (
      <div
        ref={btnRef}
        style={{
          position: "absolute",
          bottom: "20px",
          left: "12px",
          padding: "8px",
          borderRadius: "8px",
          fontSize: "15px",
          cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
          zIndex: 1000,
        }}
        onClick={onClick}
      >
        <FaCircleInfo />
      </div>
    );
  };

  // Dynamic Legend Component
  const Legend = ({ getColorForSubdivision, lineLayers }) => {
    const legendItems = useMemo(() => {
      const items = [];
      Object.keys(lineLayers).forEach(subdivision => {
        const displayName = subdivision
          .replace(/_/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        items.push({ label: displayName, color: getColorForSubdivision(subdivision) });
      });
      items.sort((a, b) => a.label.localeCompare(b.label));
      return items;
    }, [lineLayers, getColorForSubdivision]);

    if (legendItems.length === 0) {
      return null;
    }

    return <MapLegendControl sections={[{ shape: "square", items: legendItems }]} />;
  };

  return (
    <div className="mapcontainerall">
      {mapBounds && (
        <MapContainer
          bounds={mapBounds}
          style={{ height: "100%", width: "100%" }}
        >
          <MapHomeControl />
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          <LayersControl position="topright">
            {boundaryData && (
              <LayersControl.Overlay name="Country" checked>
                <GeoJSON
                  data={boundaryData}
                  style={{ color: "blue", weight: 2, fillOpacity: 0 }}
                />
              </LayersControl.Overlay>
            )}

            {lineLayers &&
              Object.entries(lineLayers).map(([subdivision, data]) => (
                <LayersControl.Overlay
                  key={subdivision}
                  name={subdivision.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  checked
                >
                  <GeoJSON
                    data={data}
                    style={() => ({
                      color: getColorForSubdivision(subdivision),
                      weight: 1.5,
                      fillOpacity: 0.5,
                    })}
                    onEachFeature={(feature, layer) => {
                      if (feature.properties) {
                        layer.bindPopup(
                          `<b>Info</b><br>${Object.entries(
                            feature.properties
                          )
                            .map(([key, value]) => `${key}: ${value}`)
                            .join("<br>")}`
                        );
                      }
                    }}
                  />
                </LayersControl.Overlay>
              ))}
          </LayersControl>

          {info && (
            <div
              ref={infoPanelRef}
              style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
            >
              This map displays various network infrastructure data, including different types of roads, water networks, and power lines.
            </div>
          )}
          <InfoButton onClick={() => setInfo(!info)} />

          <Legend
            getColorForSubdivision={getColorForSubdivision}
            lineLayers={lineLayers}
          />
        </MapContainer>
      )}
    </div>
  );
};

export default Network;